import React, { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { useAuthStore, useAuthSelector, selectIsAuthenticated } from './stores/authStore';
import { AuthProvider } from './contexts/AuthContext';
import './utils/clearAuthCache'; // Make clearAuthCache available globally

// Components (keep these as regular imports as they're used on every page)
import Navbar from './components/ui/Navbar';
import Footer from './components/ui/Footer';
import ProtectedRoute from './components/function/ProtectedRoute';
import ErrorBoundary from './components/function/ErrorBoundary';
import RouteError from './components/function/RouteError';
import ConstructionBanner from './components/ui/ConstructionBanner';
import GoogleAnalytics from './components/analytics/GoogleAnalytics';

// Import the lazy wrapper with retry logic
import { lazyWithRetry } from './utils/lazyWithRetry';

const HomePage = lazyWithRetry(() => import('./pages/HomePage'));
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const RegisterPage = lazyWithRetry(() => import('./pages/RegisterPage'));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'));
const EventsPage = lazyWithRetry(() => import('./pages/EventsPage'));
const EventDetailPage = lazyWithRetry(() => import('./pages/EventDetailPage'));
const CheckoutPage = lazyWithRetry(() => import('./pages/CheckoutPage'));
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage'));
const AlertsPage = lazyWithRetry(() => import('./pages/AlertsPage'));
const PricingPage = lazyWithRetry(() => import('./pages/PricingPage'));
const FeaturesPage = lazyWithRetry(() => import('./pages/FeaturesPage'));
const HowItWorksPage = lazyWithRetry(() => import('./pages/HowItWorksPage'));
const EventSearchPageV2 = lazyWithRetry(() => import('./pages/EventSearchPageV2'));
const EventSearchPage = lazyWithRetry(() => import('./pages/EventSearchPage'));
const ApiAccessPage = lazyWithRetry(() => import('./pages/ApiAccessPage'));
const AdminPage = lazyWithRetry(() => import('./pages/AdminPage'));
const ComingSoonPage = lazyWithRetry(() => import('./pages/ComingSoonPage'));
const EmailVerificationPage = lazyWithRetry(() => import('./pages/EmailVerificationPage'));
const VerificationPendingPage = lazyWithRetry(() => import('./pages/VerificationPendingPage'));
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage'));
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'));
const AuthCallbackPage = lazyWithRetry(() => import('./pages/AuthCallbackPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));

// Subscription pages
import SubscriptionPage from './pages/SubscriptionPage';
const SubscriptionSuccessPage = lazyWithRetry(() => import('./pages/SubscriptionSuccessPage'));
const SubscriptionManagePage = lazyWithRetry(() => import('./pages/SubscriptionManagePage'));

// Mesh Coordinator Hook (gated by feature flag)
const useMeshCoordinator = () => {
  const isMeshEnabled = import.meta.env.VITE_FEATURE_MESH === 'true';
  if (!isMeshEnabled) return { isConnected: false };
  // TODO: import actual coordinator when implemented
  return { isConnected: false };
};

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-hauk-blue-50 to-hauk-cyan-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-hauk-blue-200 border-t-hauk-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-hauk-blue-600 font-medium">Loading...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000, // renamed from cacheTime in React Query v5
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** (attempt - 1), 10_000),
    },
    mutations: {
      retry: 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** (attempt - 1), 5000),
    },
  },
});

// Layout component that includes navbar and footer
function Layout() {
  // Always call the hook, but only use the result in development
  const meshStatus = useMeshCoordinator();
  const { isConnected } = process.env.NODE_ENV === 'development' ? meshStatus : { isConnected: false };

  // Get auth state to show construction banner for logged-in users
  const isAuthenticated = useAuthSelector(selectIsAuthenticated);
  const [showBanner, setShowBanner] = React.useState(true);

  // Check if banner was previously hidden in this session
  React.useEffect(() => {
    const hidden = sessionStorage.getItem('construction-banner-hidden');
    if (hidden === 'true') {
      setShowBanner(false);
    }
  }, []);

  return (
    <AuthProvider>
      <GoogleAnalytics />
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#ffffff',
              color: '#374151',
              border: '1px solid #e5e7eb',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#ffffff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#ffffff',
              },
            },
          }}
        />

        {/* Mesh Status Indicator (Development Only) */}
        {process.env.NODE_ENV === 'development' && isConnected && (
          <div className={`fixed top-4 right-4 z-50 px-3 py-1 rounded-full text-xs font-medium ${
            isConnected ? 'bg-green-500/20 text-green-600' : 'bg-red-500/20 text-red-600'
          }`}>
            Mesh: {isConnected ? 'Connected' : 'Disconnected'}
          </div>
        )}

        {/* Skip to content link for keyboard users */}
        <a href="#main-content" className="skip-to-content">Skip to main content</a>

        <Navbar />

        <div className="flex-1 overflow-y-auto overflow-x-hidden -webkit-overflow-scrolling-touch pt-16">
          {/* Construction Banner - Show only for authenticated users */}
          {isAuthenticated && showBanner && (
            <div id="construction-banner" className="sticky top-0 z-40">
              <ConstructionBanner />
            </div>
          )}

          <main id="main-content" className="min-h-full flex flex-col">
            <div className="flex-1">
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </div>
            <Footer />
          </main>
        </div>
      </div>
    </AuthProvider>
  );
}

// Create router with future flags enabled
const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <Layout />,
      errorElement: <RouteError />,
      children: [
        // Public Routes
        { path: '/', element: <HomePage /> },
        { path: '/login', element: <LoginPage /> },
        { path: '/register', element: <RegisterPage /> },
        { path: '/features', element: <FeaturesPage /> },
        { path: '/how-it-works', element: <HowItWorksPage /> },
        { path: '/pricing', element: <PricingPage /> },
        { path: '/events', element: <EventsPage /> },
        { path: '/event/:id', element: <EventDetailPage /> },
        { path: '/terms', element: <TermsPage /> },
        { path: '/privacy', element: <PrivacyPage /> },
        { path: '/coming-soon', element: <ComingSoonPage /> },
        { path: '/search', element: <EventSearchPage /> },

        // Auth Routes
        { path: '/auth/callback', element: <AuthCallbackPage /> },
        { path: '/verify-email', element: <EmailVerificationPage /> },
        { path: '/verification-pending', element: <VerificationPendingPage /> },
        { path: '/forgot-password', element: <ForgotPasswordPage /> },
        { path: '/reset-password', element: <ResetPasswordPage /> },

        // Protected Routes
        {
          path: '/dashboard',
          element: (
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/alerts',
          element: (
            <ProtectedRoute>
              <AlertsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/api-access',
          element: (
            <ProtectedRoute>
              <ApiAccessPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/checkout',
          element: (
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/profile',
          element: (
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/admin',
          element: (
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          ),
        },

        // Subscription Routes (Protected)
        {
          path: '/subscription',
          element: (
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/subscription/success',
          element: (
            <ProtectedRoute>
              <SubscriptionSuccessPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '/subscription/manage',
          element: (
            <ProtectedRoute>
              <SubscriptionManagePage />
            </ProtectedRoute>
          ),
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

function App() {
  // Initialize auth store
  useAuthStore();

  // Handle viewport and prevent double-tap zoom
  useEffect(() => {
    // Prevent double-tap zoom on iOS
    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (e: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener('touchend', preventDoubleTapZoom, { passive: false });

    // Set viewport height for mobile (handles address bar)
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);

    // Cleanup
    return () => {
      document.removeEventListener('touchend', preventDoubleTapZoom);
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
